import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import '../App.css';

export const TermsPage = () => {
    return (
        <div className="legal-page-container">
            <div className="legal-page-content">
                <Link to="/" className="back-link">
                    <ArrowLeft size={20} />
                    <span>Back to Home</span>
                </Link>

                <h1>Terms of Service</h1>
                <p className="last-updated">Last Updated: November 25, 2025</p>

                <section>
                    <h2>1. Acceptance of Terms</h2>
                    <p>
                        By accessing and using CutIQ ("the Service"), you accept and agree to be bound by the terms and
                        provisions of this agreement. If you do not agree to these Terms of Service, please do not use the Service.
                    </p>
                </section>

                <section>
                    <h2>2. Description of Service</h2>
                    <p>
                        CutIQ is a video downloading and editing platform that allows users to:
                    </p>
                    <ul>
                        <li>Download videos from 1,848+ supported platforms (YouTube, TikTok, Instagram, etc.)</li>
                        <li>Extract audio from videos in MP3 format</li>
                        <li>Download playlists and bulk content</li>
                        <li>Access professional video editing tools (Unlimited tier only)</li>
                        <li>Track download history and usage statistics</li>
                    </ul>
                </section>

                <section>
                    <h2>3. User Accounts and Registration</h2>
                    <p>
                        To access certain features of the Service, you must register for an account. You agree to:
                    </p>
                    <ul>
                        <li>Provide accurate, current, and complete information during registration</li>
                        <li>Maintain and promptly update your account information</li>
                        <li>Maintain the security of your password and account</li>
                        <li>Accept responsibility for all activities that occur under your account</li>
                        <li>Notify us immediately of any unauthorized use of your account</li>
                    </ul>
                </section>

                <section>
                    <h2>4. Subscription Plans and Billing</h2>

                    <h3>4.1 Available Plans</h3>
                    <p>We offer the following subscription tiers:</p>
                    <ul>
                        <li><strong>Free Tier:</strong> 10 video downloads/month, 10 audio downloads/month, watermark after limit</li>
                        <li><strong>Creator 80 ($4.99/month):</strong> 80 video downloads/month, 80 audio downloads/month, no watermark within limit</li>
                        <li><strong>Unlimited ($9.99/month):</strong> Unlimited downloads, no watermarks, full video editing suite</li>
                    </ul>

                    <h3>4.2 Billing Terms</h3>
                    <ul>
                        <li>Subscriptions are billed monthly or annually in advance</li>
                        <li>Annual subscriptions receive a 20% discount</li>
                        <li>Payment is processed securely through Stripe</li>
                        <li>Usage resets on the first day of each billing cycle</li>
                        <li>Unused downloads do not roll over to the next month</li>
                    </ul>

                    <h3>4.3 Automatic Renewal</h3>
                    <p>
                        Your subscription will automatically renew at the end of each billing period unless you cancel before
                        the renewal date. You can cancel your subscription at any time through your account settings or the
                        Stripe billing portal.
                    </p>

                    <h3>4.4 Refund Policy</h3>
                    <p>
                        We offer a 14-day satisfaction guarantee. If you're not satisfied with your subscription, contact us
                        within 14 days of your initial purchase for a full refund. Refunds for subsequent billing periods are
                        handled on a case-by-case basis.
                    </p>
                </section>

                <section>
                    <h2>5. Acceptable Use Policy</h2>

                    <h3>5.1 Permitted Use</h3>
                    <p>You may use the Service only for lawful purposes and in accordance with these Terms. You agree to:</p>
                    <ul>
                        <li>Only download content that you own or have permission to download</li>
                        <li>Respect intellectual property rights of content creators</li>
                        <li>Use downloaded content in compliance with applicable copyright laws</li>
                        <li>Use the Service for personal or business purposes as intended</li>
                    </ul>

                    <h3>5.2 Prohibited Use</h3>
                    <p>You agree NOT to:</p>
                    <ul>
                        <li>Download copyrighted content without proper authorization</li>
                        <li>Use the Service to infringe on intellectual property rights</li>
                        <li>Distribute downloaded content in violation of platform terms or copyright law</li>
                        <li>Attempt to circumvent usage limits or watermarking</li>
                        <li>Reverse engineer, decompile, or disassemble any portion of the Service</li>
                        <li>Use the Service to transmit viruses, malware, or harmful code</li>
                        <li>Attempt to gain unauthorized access to our systems or user accounts</li>
                        <li>Use automated tools to scrape or abuse the Service</li>
                        <li>Resell or redistribute access to the Service without authorization</li>
                    </ul>
                </section>

                <section>
                    <h2>6. Copyright and Intellectual Property</h2>

                    <h3>6.1 Your Responsibility</h3>
                    <p>
                        <strong>Important:</strong> You are solely responsible for ensuring you have the legal right to download
                        and use any content accessed through our Service. CutIQ is a tool that facilitates downloads but does
                        not grant licenses or permissions for copyrighted content.
                    </p>

                    <h3>6.2 DMCA Compliance</h3>
                    <p>
                        We respect the intellectual property rights of others. If you believe content accessed through our Service
                        infringes your copyright, please contact us with:
                    </p>
                    <ul>
                        <li>A description of the copyrighted work</li>
                        <li>The URL or location of the infringing material</li>
                        <li>Your contact information</li>
                        <li>A statement of good faith belief that the use is unauthorized</li>
                        <li>A statement that the information is accurate and you are authorized to act</li>
                    </ul>

                    <h3>6.3 Our Intellectual Property</h3>
                    <p>
                        The Service, including its design, functionality, code, and branding, is owned by The Trend Junkies and
                        protected by copyright, trademark, and other intellectual property laws.
                    </p>
                </section>

                <section>
                    <h2>7. Usage Limits and Watermarking</h2>
                    <ul>
                        <li>Download limits are enforced based on your subscription tier</li>
                        <li>Free tier users receive watermarked content after exceeding their monthly limit</li>
                        <li>Watermarks are automatically removed when you upgrade to a paid plan</li>
                        <li>Video editing features are exclusive to Unlimited tier subscribers</li>
                        <li>We reserve the right to modify usage limits with 30 days notice</li>
                    </ul>
                </section>

                <section>
                    <h2>8. Third-Party Services</h2>
                    <p>
                        The Service integrates with third-party platforms and services, including but not limited to:
                    </p>
                    <ul>
                        <li>YouTube, TikTok, Instagram, and other video platforms (for downloads via yt-dlp)</li>
                        <li>Supabase (for authentication and data storage)</li>
                        <li>Stripe (for payment processing)</li>
                    </ul>
                    <p>
                        <strong>YouTube Terms of Service:</strong> By using our Service to access or upload content to YouTube,
                        you agree to be bound by the <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a>.
                    </p>
                    <p>
                        Your use of these third-party services is subject to their respective terms of service and privacy policies.
                        We are not responsible for the practices or content of third-party services.
                    </p>
                </section>

                <section>
                    <h2>9. Data and Privacy</h2>
                    <p>
                        Your privacy is important to us. Our collection and use of personal information is described in our{' '}
                        <Link to="/privacy">Privacy Policy</Link>. By using the Service, you consent to our privacy practices.
                    </p>
                </section>

                <section>
                    <h2>10. Disclaimer of Warranties</h2>
                    <p>
                        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
                        INCLUDING BUT NOT LIMITED TO:
                    </p>
                    <ul>
                        <li>Warranties of merchantability or fitness for a particular purpose</li>
                        <li>Availability, reliability, or uptime guarantees</li>
                        <li>Accuracy or completeness of content</li>
                        <li>Freedom from errors, bugs, or interruptions</li>
                    </ul>
                </section>

                <section>
                    <h2>11. Limitation of Liability</h2>
                    <p>
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE TREND JUNKIES SHALL NOT BE LIABLE FOR:
                    </p>
                    <ul>
                        <li>Indirect, incidental, special, consequential, or punitive damages</li>
                        <li>Loss of profits, data, use, or goodwill</li>
                        <li>Service interruptions or downtime</li>
                        <li>User errors or misuse of the Service</li>
                        <li>Damages resulting from third-party content or services</li>
                    </ul>
                    <p>
                        Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim,
                        or $100, whichever is greater.
                    </p>
                </section>

                <section>
                    <h2>12. Indemnification</h2>
                    <p>
                        You agree to indemnify and hold harmless The Trend Junkies, its affiliates, and their respective officers,
                        directors, and employees from any claims, damages, losses, liabilities, and expenses (including legal fees)
                        arising from:
                    </p>
                    <ul>
                        <li>Your use of the Service</li>
                        <li>Your violation of these Terms</li>
                        <li>Your infringement of intellectual property or other rights</li>
                        <li>Your content or actions on the Service</li>
                    </ul>
                </section>

                <section>
                    <h2>13. Termination</h2>

                    <h3>13.1 Termination by You</h3>
                    <p>
                        You may terminate your account at any time through your account settings. Upon termination, your
                        subscription will continue until the end of your current billing period.
                    </p>

                    <h3>13.2 Termination by Us</h3>
                    <p>
                        We reserve the right to suspend or terminate your account immediately, without prior notice, if:
                    </p>
                    <ul>
                        <li>You violate these Terms of Service</li>
                        <li>You engage in fraudulent or illegal activity</li>
                        <li>Your account is inactive for an extended period</li>
                        <li>We cease operations or discontinue the Service</li>
                    </ul>
                </section>

                <section>
                    <h2>14. Changes to Terms</h2>
                    <p>
                        We reserve the right to modify these Terms at any time. We will notify users of material changes via
                        email or in-app notification. Your continued use of the Service after changes take effect constitutes
                        acceptance of the updated Terms.
                    </p>
                </section>

                <section>
                    <h2>15. Governing Law and Dispute Resolution</h2>
                    <p>
                        These Terms shall be governed by and construed in accordance with the laws of the State of [Your State],
                        without regard to its conflict of law provisions.
                    </p>
                    <p>
                        Any disputes arising from these Terms or the Service shall be resolved through binding arbitration in
                        accordance with the American Arbitration Association rules, except that either party may seek injunctive
                        relief in court for intellectual property violations.
                    </p>
                </section>

                <section>
                    <h2>16. Contact Information</h2>
                    <p>
                        If you have questions about these Terms, please contact us:
                    </p>
                    <ul>
                        <li><strong>Email:</strong> support@cutiq.io</li>
                        <li><strong>Company:</strong> The Trend Junkies</li>
                        <li><strong>Address:</strong> [Your Business Address]</li>
                    </ul>
                </section>

                <section>
                    <h2>17. Miscellaneous</h2>
                    <ul>
                        <li><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions continue in effect</li>
                        <li><strong>Waiver:</strong> Our failure to enforce any right does not waive that right</li>
                        <li><strong>Assignment:</strong> You may not assign these Terms; we may assign them without restriction</li>
                        <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and us</li>
                    </ul>
                </section>

                <div className="legal-footer">
                    <p>
                        By using CutIQ, you acknowledge that you have read, understood, and agree to be bound by these
                        Terms of Service.
                    </p>
                </div>
            </div>
        </div>
    );
};
